// Abhaken offline (Phase 6): ohne Netz merkt sich die App jede Zeile mit ihrem letzten Zustand in localStorage
// (je Person) und legt das ueber die Liste aus dem Cache. Sobald wieder Netz da ist, traegt sie alles nach.
// "Zuletzt gewinnt": wer offline abhakt, ueberschreibt beim Nachtragen eine zwischenzeitliche Aenderung des
// anderen an derselben Zeile. Der Server-Endpunkt setzt Zustaende (nicht umschalten), Wiederholen ist harmlos.
import type { EinkaufListe, EinkaufPosten } from './client';

export type Warteschlange = Record<string, boolean>;
const schluessel = (n: string) => `nebenbei2-abhaken:${n}`;
export const WARTESCHLANGE_EREIGNIS = 'nebenbei2-warteschlange';

export function lesen(n: string): Warteschlange {
  try {
    const roh = localStorage.getItem(schluessel(n));
    const w: unknown = roh ? JSON.parse(roh) : {};
    return typeof w === 'object' && w !== null ? (w as Warteschlange) : {};
  } catch {
    return {};
  }
}

function schreiben(n: string, w: Warteschlange): void {
  if (Object.keys(w).length === 0) localStorage.removeItem(schluessel(n));
  else localStorage.setItem(schluessel(n), JSON.stringify(w));
  window.dispatchEvent(new Event(WARTESCHLANGE_EREIGNIS));
}

export function vormerken(n: string, ids: readonly string[], abgehakt: boolean): void {
  const w = lesen(n);
  for (const id of ids) w[id] = abgehakt;
  schreiben(n, w);
}

/** Entfernt nur Eintraege, die seit dem Senden nicht erneut geaendert wurden. */
export function erledigt(n: string, gesendet: Warteschlange): void {
  const w = lesen(n);
  for (const [id, wert] of Object.entries(gesendet)) if (w[id] === wert) delete w[id];
  schreiben(n, w);
}

/** Zaehlung und Gruppenstand aus den Zeilen neu rechnen (fuer das sofortige Umschalten und die Warteschlange). */
export function mitZustaenden(l: EinkaufListe, zustand: ReadonlyMap<string, boolean>): EinkaufListe {
  if (zustand.size === 0) return l;
  const setze = (p: EinkaufPosten): EinkaufPosten => {
    const z = zustand.get(p.id);
    return z === undefined || z === p.abgehakt ? p : { ...p, abgehakt: z };
  };
  const rezepte = l.rezepte.map((r) => {
    const posten = r.posten.map(setze);
    const offen = posten.filter((p) => !p.abgehakt).length;
    return { ...r, posten, offen, vorhanden: posten.length - offen };
  });
  const freie = l.freie.map(setze);
  const status = new Map<string, boolean>();
  for (const p of [...freie, ...rezepte.flatMap((r) => r.posten)]) status.set(p.id, p.abgehakt);
  const gruppen = l.gruppen.map((g) => ({ ...g, offen: g.posten_ids.filter((id) => status.get(id) === false).length }));
  return {
    ...l,
    rezepte,
    freie,
    gruppen,
    offen: gruppen.filter((g) => g.offen > 0).length,
    abgehakt: gruppen.filter((g) => g.offen === 0).length,
  };
}

export function mitWarteschlange(l: EinkaufListe, n: string): EinkaufListe {
  return mitZustaenden(l, new Map(Object.entries(lesen(n))));
}

// ---------------------------------------------------------------- Eigene Eintraege offline (QA Runde 2 N1)
// Ohne Netz angelegte eigene Eintraege liegen wie die Haken in localStorage (je Person) und ueberleben Schliessen
// und Neuladen. Jeder bekommt auf dem Geraet seine ID; der Server legt dieselbe ID nur einmal an, Nachtragen ist
// darum wiederholbar, auch wenn eine Antwort unterwegs verloren geht.

export interface WartenderEintrag {
  id: string;
  name: string;
}
const eintragSchluessel = (n: string) => `nebenbei2-eintraege:${n}`;

function istWartend(x: unknown): x is WartenderEintrag {
  return typeof x === 'object' && x !== null && typeof (x as WartenderEintrag).id === 'string' && typeof (x as WartenderEintrag).name === 'string';
}

export function eintraegeLesen(n: string): WartenderEintrag[] {
  try {
    const roh: unknown = JSON.parse(localStorage.getItem(eintragSchluessel(n)) ?? '[]');
    return Array.isArray(roh) ? roh.filter(istWartend) : [];
  } catch {
    return [];
  }
}

function eintraegeSchreiben(n: string, liste: readonly WartenderEintrag[]): void {
  if (liste.length === 0) localStorage.removeItem(eintragSchluessel(n));
  else localStorage.setItem(eintragSchluessel(n), JSON.stringify(liste));
  window.dispatchEvent(new Event(WARTESCHLANGE_EREIGNIS));
}

export function eintragVormerken(n: string, e: WartenderEintrag): void {
  const liste = eintraegeLesen(n).filter((x) => x.id !== e.id);
  eintraegeSchreiben(n, [...liste, e]);
}

export function eintragErledigt(n: string, id: string): void {
  eintraegeSchreiben(n, eintraegeLesen(n).filter((x) => x.id !== id));
}

/** UUID v4 auch ohne sicheren Kontext (http://nebenbei2.local hat kein crypto.randomUUID). */
export function neueId(): string {
  const b = new Uint8Array(16);
  crypto.getRandomValues(b);
  b[6] = ((b[6] ?? 0) & 0x0f) | 0x40;
  b[8] = ((b[8] ?? 0) & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Netzfehler (kein Server erreicht) im Unterschied zu einer Antwort mit Fehlercode. */
export function istNetzfehler(f: unknown): boolean {
  return f instanceof TypeError || (typeof navigator !== 'undefined' && navigator.onLine === false && !(f instanceof Error && 'status' in f));
}
